import React, { useState } from 'react';
import ApplicationSuccess from './ApplicationSuccess';
import careerService from '../../api/careerService';

const JobApplicationForm = ({ isOpen, onClose, jobTitle, description }) => {
    const [formData, setFormData] = useState({
        fullName: '',
        email: '',
        phone_no: '',
        address: '',
        description: '',
        resume: null
    });
    
    const [showSuccess, setShowSuccess] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [formErrors, setFormErrors] = useState({});

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (!file) return;

        const allowedTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
        if (!allowedTypes.includes(file.type)) {
            setFormErrors(prev => ({ ...prev, resume: 'Only PDF, DOC, and DOCX files are allowed' }));
            e.target.value = '';
            return;
        }

        const maxSize = 5 * 1024 * 1024; // 5MB
        if (file.size > maxSize) {
            setFormErrors(prev => ({ ...prev, resume: 'File size must be less than 5MB' }));
            e.target.value = '';
            return;
        }

        setFormErrors(prev => ({ ...prev, resume: undefined }));
        setFormData(prev => ({
            ...prev,
            resume: file
        }));
    };

    const validateForm = () => {
        const errors = {};
        
        // Validate fullName
        if (!formData.fullName.trim()) {
            errors.fullName = 'Name is required';
        }
        
        // Validate email
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!formData.email.trim()) {
            errors.email = 'Email is required';
        } else if (!emailRegex.test(formData.email)) {
            errors.email = 'Please enter a valid email';
        }
        
        // Validate phone
        const phoneRegex = /^\d{10}$/;
        if (!formData.phone_no.trim()) {
            errors.phone_no = 'Phone number is required';
        } else if (!phoneRegex.test(formData.phone_no.replace(/[\s-]/g, ''))) {
            errors.phone_no = 'Please enter a valid 10-digit phone number';
        }
        
        // Validate address
        if (!formData.address.trim()) {
            errors.address = 'Address is required';
        }
        
        // Validate description
        if (!formData.description.trim()) {
            errors.description = 'Description is required';
        }
        
        // Validate resume
        if (!formData.resume) {
            errors.resume = 'Resume is required';
        }
        
        setFormErrors(errors);
        return Object.keys(errors).length === 0;
    };
    
    const handleSubmit = async (e) => {
        e.preventDefault();
        setError(null);
        
        // Validate form
        if (!validateForm()) {
            return;
        }
        
        setIsLoading(true);
        
        try {
            // Submit form data to API
            await careerService.applyForJob(formData);
            setShowSuccess(true);
        } catch (err) {
            setError(
                err.response?.data?.message || 
                'Failed to submit your application. Please try again.'
            );
        } finally {
            setIsLoading(false);
        }
    };
    
    // Reset form data and errors
    const resetForm = () => {
        setFormData({
            fullName: '',
            email: '',
            phone_no: '',
            address: '',
            description: '',
            resume: null
        });
        setFormErrors({});
        setError(null);
    };

    // Handle dialog close from success screen
    const handleSuccessClose = (action) => {
        setShowSuccess(false);
        if (action === 'explore') {
            // Logic to explore more jobs
        }
        resetForm();
        onClose();
    };

    if (!isOpen) return null;
    
    // Show success dialog if form was submitted
    if (showSuccess) {
        return <ApplicationSuccess isOpen={showSuccess} onClose={handleSuccessClose} />;
    }

    return (
        <div className="fixed inset-0 bg-[#F5F5F5] bg-opacity-95 flex items-center justify-center z-50 p-0">
                <div className="relative bg-white rounded-[24px] border border-gray-200 px-[20px] py-8 w-[90%] max-w-[755px] mx-auto my-auto"
                style={{
                    maxHeight: '90vh',
                    overflowY: 'auto',
                    scrollbarWidth: 'thin',
                    scrollbarColor: '#888 #f1f1f1'
                }}
            >
                <button
                    onClick={() => {
                        resetForm();
                        onClose();
                    }}
                    className="absolute top-4 right-4 text-gray-500 hover:text-gray-700 focus:outline-none z-10"
                    aria-label="Close"
                >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                </button>
                <h2 className="font-inter font-medium text-[18px] leading-[150%] mb-2">
                    #{jobTitle || 'Agronomy Sales Representative'}
                </h2>

                <p className="font-inter font-normal text-[14px] leading-[150%] mb-4 text-[#ABABAB]">
                    {description || 'No description available'}
                </p>

                <form onSubmit={handleSubmit} className="flex flex-col gap-[20px] md:gap-[32px]">
                    <div className="flex flex-row gap-[10px] md:gap-[32px]">
                        <div className="flex-1">
                            <label htmlFor="fullName" className="font-inter font-normal text-[14px] md:text-[16px] leading-[100%] block mb-2">
                                Your Name
                            </label>
                            <input
                                type="text"
                                id="fullName"
                                name="fullName"
                                value={formData.fullName}
                                onChange={handleChange}
                                placeholder="Name"
                                className={`w-full h-[40px] rounded-[80px] border ${formErrors.fullName ? 'border-red-500' : 'border-gray-300'} px-4 py-2 font-inter text-[12px] md:text-[16px] focus:outline-none focus:ring-1 focus:ring-red-400`}
                                required
                            />
                            {formErrors.fullName && (
                                <p className="text-red-500 text-xs mt-1 ml-4">{formErrors.fullName}</p>
                            )}
                        </div>

                        <div className="flex-1">
                            <label htmlFor="email" className="font-inter font-normal text-[14px] md:text-[16px] leading-[100%] block mb-2">
                                Your Email
                            </label>
                            <input
                                type="email"
                                id="email"
                                name="email"
                                value={formData.email}
                                onChange={handleChange}
                                placeholder="Email"
                                className={`w-full h-[40px] rounded-[80px] border ${formErrors.email ? 'border-red-500' : 'border-gray-300'} px-4 py-2 font-inter text-[12px] md:text-[16px] focus:outline-none focus:ring-1 focus:ring-gray-400`}
                                required
                            />
                            {formErrors.email && (
                                <p className="text-red-500 text-xs mt-1 ml-4">{formErrors.email}</p>
                            )}
                        </div>
                    </div>
                    <div>
                        <label htmlFor="mobile" className="font-inter font-normal text-[14px] md:text-[16px] leading-[100%] block mb-2">
                            Your Mobile Number
                        </label>
                        <input
                            type="tel"
                            id="phone_no"
                            name="phone_no"
                            value={formData.phone_no}
                            onChange={handleChange}
                            placeholder="Mobile Number"
                            className={`w-full h-[40px] rounded-[80px] border ${formErrors.phone_no ? 'border-red-500' : 'border-gray-300'} px-4 py-2 font-inter text-[12px] md:text-[16px] focus:outline-none focus:ring-1 focus:ring-gray-400`}
                            required
                        />
                        {formErrors.phone_no && (
                            <p className="text-red-500 text-xs mt-1 ml-4">{formErrors.phone_no}</p>
                        )}
                    </div>

                    <div>
                        <label htmlFor="address" className="font-inter font-normal text-[14px] md:text-[16px] leading-[100%] block mb-2">
                            Your Address
                        </label>
                        <input
                            type="text"
                            id="address"
                            name="address"
                            value={formData.address}
                            onChange={handleChange}
                            placeholder="Address"
                            className={`w-full h-[40px] rounded-[80px] border ${formErrors.address ? 'border-red-500' : 'border-gray-300'} px-4 py-2 font-inter text-[12px] md:text-[16px] focus:outline-none focus:ring-1 focus:ring-gray-400`}
                            required
                        />
                        {formErrors.address && (
                            <p className="text-red-500 text-xs mt-1 ml-4">{formErrors.address}</p>
                        )}
                    </div>

                    <div>
                        <label htmlFor="description" className="font-inter font-normal text-[14px] md:text-[16px] leading-[100%] block mb-2">
                            Description
                        </label>
                        <textarea
                            id="description"
                            name="description"
                            value={formData.description}
                            onChange={handleChange}
                            placeholder="Why do you want to join Agastya"
                            className={`w-full min-h-[100px] rounded-[20px] border ${formErrors.description ? 'border-red-500' : 'border-gray-300'} px-4 py-2 font-inter text-[12px] md:text-[16px] focus:outline-none focus:ring-1 focus:ring-gray-400`}
                            required
                        />
                        {formErrors.description && (
                            <p className="text-red-500 text-xs mt-1 ml-4">{formErrors.description}</p>
                        )}
                    </div>

                    <div>
                        <label htmlFor="resume" className="font-inter font-normal text-[14px] md:text-[16px] leading-[100%] block mb-2">
                            Resume
                        </label>
                        <div className={`w-full min-h-[118px] rounded-[20px] border ${formErrors.resume ? 'border-red-500' : 'border-gray-300'} px-4 py-2 font-inter text-[14px] flex`}>
                            <input
                                type="file"
                                id="resume"
                                name="resume"
                                onChange={handleFileChange}
                                className="hidden"
                                accept=".pdf,.doc,.docx"
                                required
                            />
                            <label htmlFor="resume" className="flex gap-2 cursor-pointer ">
                                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="19" viewBox="0 0 18 19" fill="none">
                                    <g opacity="0.2">
                                        <path d="M15.6604 8.80711L8.94815 15.5194C8.12584 16.3417 7.01056 16.8036 5.84765 16.8036C4.68474 16.8036 3.56946 16.3417 2.74716 15.5194C1.92486 14.6971 1.46289 13.5818 1.46289 12.4189C1.46289 11.256 1.92486 10.1407 2.74716 9.31838L9.00658 3.05896C9.55478 2.50979 10.2987 2.20088 11.0746 2.2002C11.8506 2.19951 12.5951 2.5071 13.1442 3.0553C13.6934 3.60351 14.0023 4.34741 14.003 5.12337C14.0037 5.89933 13.6961 6.64378 13.1479 7.19295L6.87385 13.4524C6.59975 13.7265 6.22799 13.8805 5.84035 13.8805C5.45271 13.8805 5.08095 13.7265 4.80685 13.4524C4.53275 13.1783 4.37876 12.8065 4.37876 12.4189C4.37876 12.0312 4.53275 11.6595 4.80685 11.3854L11.0078 5.19169" stroke="#1C1C1C" strokeWidth="1.46077" strokeLinecap="round" strokeLinejoin="round"/>
                                    </g>
                                </svg>
                                <span className="font-inter text-[12px] md:text-[16px] text-[#34343363]">
                                    {formData.resume ? formData.resume.name : 'Choose File or Drop a file Here'}
                                </span>
                                {formErrors.resume && (
                                    <span className="text-red-500 text-xs ml-2">{formErrors.resume}</span>
                                )}
                            </label>
                        </div>
                    </div>

                    {error && (
                        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative" role="alert">
                            <span className="block sm:inline">{error}</span>
                        </div>
                    )}
                    
                    <div className="flex justify-start">
                        <button
                            type="submit"
                            disabled={isLoading}
                            className="w-[130px] h-[37px] bg-black text-white font-inter font-normal text-[14px] md:text-[16px] rounded-full px-4 py-1 flex items-center justify-center gap-[2px] hover:bg-gray-800 transition-colors disabled:bg-gray-500 disabled:cursor-not-allowed"
                        >
                            {isLoading ? (
                                <span className="flex items-center">
                                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                    </svg>
                                    Submitting...
                                </span>
                            ) : (
                                <>
                                    <span>Apply Now</span>
                                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 21 22" fill="none">
                                        <path d="M7.3125 13.9575L13.3365 7.93349" stroke="white" strokeWidth="1.21704" strokeLinecap="round" strokeLinejoin="round"/>
                                        <path d="M7.3125 7.93213H13.3365V13.9562" stroke="white" strokeWidth="1.21704" strokeLinecap="round" strokeLinejoin="round"/>
                                    </svg>
                                </>
                            )}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default JobApplicationForm;
